import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Hook to handle table filters
 * Stores the selected filters in the URL search params
 * Also provides utility to update and delete the filters from the URL
 */
const useFiltersBySearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const clearSearchParams = useCallback(() => {
    const params = new URLSearchParams();
    setSearchParams(params.toString(), { replace: true });
  }, [setSearchParams]);

  const updateSearchParamsByKey = useCallback(
    (values: { [searchKey: string]: string | string[] | undefined }) => {
      if (!Object.keys(values).length) {
        return;
      }
      setSearchParams(
        prevParams => {
          const params = new URLSearchParams(prevParams);
          Object.entries(values).forEach(([searchKey, value]) => {
            if (value === undefined) {
              // Remove the parameter completely
              params.delete(searchKey);
            } else if (Array.isArray(value)) {
              params.delete(searchKey);
              value.forEach(val => params.append(searchKey, val));
            } else {
              // Assuming it is string or undefined
              params.set(searchKey, value);
            }
          });
          return params.toString();
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const deleteSearchParamsByKeyValue = useCallback(
    (values: { [key: string]: string | undefined }) => {
      if (!Object.keys(values).length) {
        return;
      }
      setSearchParams(
        prevParams => {
          const params = new URLSearchParams(prevParams);
          Object.entries(values).forEach(([searchKey, value]) => {
            params.delete(searchKey, value);
          });
          return params.toString();
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return {
    searchParams,
    updateSearchParamsByKey,
    deleteSearchParamsByKeyValue,
    clearSearchParams,
  };
};

export default useFiltersBySearchParams;
